import React, { createContext, useEffect, useMemo, useState, } from "react";
import Vector from "ol/layer/Vector";
import useLayerEvents from "../hooks/useLayerEvents";
import useLayerOpacity from "../hooks/useLayerOpacity";
import useLayerZIndex from "../hooks/useLayerZIndex";
import { useMap } from "../map/MapProvider";
/** Context providing access to the vector layer. */
export const VectorLayerContext = createContext({});
/** Context providing the setSource function to maintain the vector layer source.  */
export const VectorLayerSourceContext = createContext({});
/**
 * Create an openlayers Vector layer. Add a source as child of VectorLayer.
 */
export default function VectorLayer({ zIndex = 0, opacity = 1, attributions, declutter = false, minZoom, maxZoom, renderOrder, renderBuffer = 100, updateWhileAnimating = false, updateWhileInteracting = false, extent, style, visible = true, children, onPreRender, onPostRender, }) {
    const map = useMap();
    const [layer, setLayer] = useState();
    const [source, setSource] = useState();
    const setLayerZIndex = useLayerZIndex(zIndex);
    const setLayerOpacity = useLayerOpacity(opacity);
    const setLayerEvents = useLayerEvents({ onPreRender, onPostRender });
    const sourceContext = useMemo(() => ({
        setSource: setSource,
    }), []);
    useEffect(() => {
        layer === null || layer === void 0 ? void 0 : layer.setStyle(style);
    }, [layer, style]);
    useEffect(() => {
        layer === null || layer === void 0 ? void 0 : layer.setVisible(visible);
    }, [layer, visible]);
    useEffect(() => {
        var _a;
        (_a = layer === null || layer === void 0 ? void 0 : layer.getSource()) === null || _a === void 0 ? void 0 : _a.setAttributions(attributions);
    }, [layer, attributions]);
    useEffect(() => {
        if (source)
            layer === null || layer === void 0 ? void 0 : layer.setSource(source);
    }, [layer, source]);
    const context = useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        layer: layer,
        source: source,
    }), [layer, source]);
    useEffect(() => {
        const layer = new Vector({
            declutter,
            minZoom,
            maxZoom,
            renderOrder,
            renderBuffer,
            updateWhileAnimating,
            updateWhileInteracting,
            extent,
        });
        setLayerEvents(layer);
        setLayerZIndex(layer);
        setLayerOpacity(layer);
        setLayer(layer);
        map.addLayer(layer);
        return () => {
            map.removeLayer(layer);
            setLayerEvents(undefined);
            setLayerZIndex(undefined);
            setLayerOpacity(undefined);
            setLayer(undefined);
        };
    }, [
        map,
        declutter,
        minZoom,
        maxZoom,
        renderOrder,
        renderBuffer,
        updateWhileAnimating,
        updateWhileInteracting,
        extent,
        setLayerEvents,
        setLayerZIndex,
        setLayerOpacity,
    ]);
    if (!layer)
        return React.createElement(React.Fragment, null);
    return (React.createElement(VectorLayerContext.Provider, { value: context },
        React.createElement(VectorLayerSourceContext.Provider, { value: sourceContext }, children)));
}
